import type { ElementType, ReactNode } from "react";

/**
 * Typography that maps 1:1 to the design system's composite type tokens
 * (`--type-*` in design/styles/index.css). Feature code uses this instead of
 * raw Tailwind size/weight utilities (text-sm, font-semibold...), which
 * scripts/check-conventions.ts flags outside src/components.
 */

export type TextVariant =
  | "display-2xl"
  | "display-xl"
  | "h1"
  | "h2"
  | "h3"
  | "body-lg"
  | "body"
  | "body-sm"
  | "caption"
  | "eyebrow"
  | "code";

export type TextTone = "primary" | "secondary" | "tertiary" | "error" | "success" | "warning";

// Literal class strings (not template-built) so Tailwind can see them.
const VARIANT_CLASS: Record<TextVariant, string> = {
  "display-2xl": "[font:var(--type-display-2xl)]",
  "display-xl": "[font:var(--type-display-xl)]",
  h1: "[font:var(--type-h1)]",
  h2: "[font:var(--type-h2)]",
  h3: "[font:var(--type-h3)]",
  "body-lg": "[font:var(--type-body-lg)]",
  body: "[font:var(--type-body)]",
  "body-sm": "[font:var(--type-body-sm)]",
  caption: "[font:var(--type-caption)]",
  eyebrow: "[font:var(--type-eyebrow)] uppercase tracking-wide",
  code: "[font:var(--type-code)]",
};

const TONE_CLASS: Record<TextTone, string> = {
  primary: "text-content-primary",
  secondary: "text-content-secondary",
  tertiary: "text-content-tertiary",
  error: "text-error-on-subtle",
  success: "text-success-on-subtle",
  warning: "text-warning-on-subtle",
};

const DEFAULT_ELEMENT: Partial<Record<TextVariant, ElementType>> = {
  "display-2xl": "h1",
  "display-xl": "h1",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  code: "code",
};

export interface TextProps {
  variant?: TextVariant;
  tone?: TextTone;
  /** Override the rendered element (defaults: headings → h1-h3, code → code, else p). */
  as?: ElementType;
  role?: string;
  /** For aria-describedby (e.g. a field's error text). */
  id?: string;
  /** Semibold body text (titles in cards and rows) without a heading's size. */
  weight?: "regular" | "semibold";
  /** Extra classes merged after the variant/tone classes (e.g. truncate). */
  className?: string;
  children: ReactNode;
}

export function Text({
  variant = "body",
  tone = "primary",
  weight = "regular",
  as,
  role,
  id,
  className = "",
  children,
}: Readonly<TextProps>) {
  const Element = as ?? DEFAULT_ELEMENT[variant] ?? "p";
  return (
    <Element
      role={role}
      id={id}
      className={`${VARIANT_CLASS[variant]} ${TONE_CLASS[tone]} ${weight === "semibold" ? "![font-weight:var(--weight-semibold)]" : ""} ${className}`.trim()}
    >
      {children}
    </Element>
  );
}
